import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { PasswordCriteriaList, getPasswordCriteria } from 'components/ui/PasswordCriteriaList';
import { emailSchema, resetPasswordSchema } from 'schemas/changePasswordSchema';

type EmailFormValues = z.infer<typeof emailSchema>;
type ResetFormValues = z.infer<typeof resetPasswordSchema>;

interface Props {
  onBack: () => void;
}

export const ForgotPassword = ({ onBack }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const resetForm = useForm<ResetFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = resetForm.watch('newPassword');
  const confirmPasswordValue = resetForm.watch('confirmPassword');
  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch = newPasswordValue === confirmPasswordValue;

  const handleSendCode = async ({ email }: EmailFormValues) => {
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setEmail(email);
      resetForm.reset();
      setCodeSent(true);
      toast.success(t('toast.verificationCodeSent'));
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.failedSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async ({ code, newPassword }: ResetFormValues) => {
    setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      toast.success(t('toast.passwordResetSuccess'));
      onBack();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.failedResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    emailForm.reset({ email });
    resetForm.reset();
    setCodeSent(false);
  };

  if (!codeSent) {
    return (
      <form className="space-y-5" onSubmit={emailForm.handleSubmit(handleSendCode)}>
        <p className="text-sm text-slate-500">{t('forgotPassword.description')}</p>

        <div className="space-y-2">
          <Label htmlFor="forgot-email">{t('forgotPassword.email')}</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            placeholder={t('forgotPassword.emailPlaceholder')}
            disabled={loading}
            {...emailForm.register('email')}
          />
          {emailForm.formState.errors.email && (
            <p className="text-xs text-red-500">
              {t(emailForm.formState.errors.email.message ?? '')}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="h-12 w-full rounded-2xl text-base font-semibold"
            disabled={loading}
          >
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            {loading ? t('forgotPassword.sending') : t('forgotPassword.sendCode')}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-2xl text-base"
            disabled={loading}
            onClick={onBack}
          >
            {t('forgotPassword.backToLogin')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="max-h-[calc(100svh-260px)] overflow-y-auto">
      <form className="space-y-5 p-2" onSubmit={resetForm.handleSubmit(handleResetPassword)}>
        <p className="text-sm text-slate-500">
          {t('forgotPassword.verificationCodeDescription', { email })}
        </p>

        <div className="space-y-2">
          <Label htmlFor="code">{t('forgotPassword.verificationCode')}</Label>
          <Input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('forgotPassword.verificationCodePlaceholder')}
            disabled={loading}
            {...resetForm.register('code')}
          />
          {resetForm.formState.errors.code && (
            <p className="text-xs text-red-500">
              {t(resetForm.formState.errors.code.message ?? '')}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">{t('forgotPassword.newPassword')}</Label>
          <Input
            id="newPassword"
            isPassword
            placeholder={t('forgotPassword.newPasswordPlaceholder')}
            disabled={loading}
            {...resetForm.register('newPassword')}
          />
          {resetForm.formState.errors.newPassword && (
            <p className="text-xs text-red-500">
              {t(resetForm.formState.errors.newPassword.message ?? '')}
            </p>
          )}
        </div>

        <PasswordCriteriaList password={newPasswordValue} />

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">{t('forgotPassword.confirmPassword')}</Label>
          <Input
            id="confirmPassword"
            isPassword
            placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
            disabled={loading}
            {...resetForm.register('confirmPassword')}
          />
          {resetForm.formState.errors.confirmPassword && (
            <p className="text-xs text-red-500">
              {t(resetForm.formState.errors.confirmPassword.message ?? '')}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="h-12 w-full rounded-2xl text-base font-semibold"
            disabled={!allCriteriaMet || !passwordsMatch || !confirmPasswordValue || loading}
          >
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            {loading ? t('forgotPassword.resetting') : t('forgotPassword.resetPassword')}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-2xl text-base"
            disabled={loading}
            onClick={handleResendCode}
          >
            {t('forgotPassword.resendCode')}
          </Button>
        </div>
      </form>
    </div>
  );
};
